import type ExtensionEventType from 'owa-service/lib/contract/ExtensionEventType';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import getExtensibilityState from '../store/getExtensibilityState';
import type { IAddinCommand, IAddin } from 'owa-addins-types';
import type LaunchEventType from 'owa-service/lib/contract/LaunchEventType';
import type { ExtensionInstallScope } from 'owa-addins-contracts';
import type { MailboxInfo } from 'owa-client-types';

const UserInstallScope: ExtensionInstallScope = 'User';
const OrgInstallScope: ExtensionInstallScope = 'Organization';
const DefaultInstallScope: ExtensionInstallScope = 'Default';
const OrgInstallScopeMetaOS: ExtensionInstallScope = 'Tenant';
const PublicInstallScopeMetaOS: ExtensionInstallScope = 'Public';

export default function getExtensionEventByType(
    type: ExtensionEventType,
    mailboxInfo: MailboxInfo,
    launchEventType?: LaunchEventType
): IAddinCommand[] {
    const enabledAddinCommands = getExtensibilityState(mailboxInfo).EnabledAddinCommands;
    if (!enabledAddinCommands) {
        return [];
    }

    let eventAddins = enabledAddinCommands.getExtensionPoint(ExtensibilityModeEnum.TrapOnSendEvent);

    if (launchEventType) {
        const launchEventAddins = enabledAddinCommands
            .getAutoRunAddinsByLaunchEventType(launchEventType)
            .map((addin: IAddin) => {
                return addin.Id;
            });

        // When we have the same add-in with both a send launch event and itemSend command, the launch event takes preference.
        // Filter out such add-ins from the itemSend list
        eventAddins = eventAddins.filter((addin: IAddin) => !launchEventAddins.includes(addin.Id));
    }
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (23,5): Type '(IAddinCommand | undefined)[]' is not assignable to type 'IAddinCommand[]'.
    // @ts-expect-error
    return eventAddins.map(addin => {
        return addin?.AddinCommands.get(type.toString());
    });
}

/*
    OnSend (ItemSend) Addin execution Order: User -> Default -> Organization
    Sorting within each scope: Increasing order by Addin Id
*/
export function getOrderedExtensionEventAddinCommands(
    type: ExtensionEventType,
    mailboxInfo: MailboxInfo,
    extensionInstallationScope?: ExtensionInstallScope,
    launchEventType?: LaunchEventType
): IAddinCommand[] {
    const itemSendAddinCommands = getExtensionEventByType(
        type,
        mailboxInfo,
        launchEventType
    ) as IAddinCommand[];

    const userInstalledAddinCommands = itemSendAddinCommands.filter(
        addinCommand =>
            addinCommand.get_OriginString?.()?.toLowerCase() === UserInstallScope.toLowerCase()
    );
    const defaultInstalledAddinCommands = itemSendAddinCommands.filter(
        addinCommand =>
            addinCommand.get_OriginString?.()?.toLowerCase() ===
            (DefaultInstallScope || PublicInstallScopeMetaOS).toLowerCase()
    );
    const organizationInstalledAddinCommands = itemSendAddinCommands.filter(
        addinCommand =>
            addinCommand.get_OriginString?.()?.toLowerCase() ==
            (OrgInstallScope || OrgInstallScopeMetaOS).toLowerCase()
    );

    userInstalledAddinCommands.sort((left, right) => {
        return addinIdCompare(left, right);
    });
    defaultInstalledAddinCommands.sort((left, right) => {
        return addinIdCompare(left, right);
    });
    organizationInstalledAddinCommands.sort((left, right) => {
        return addinIdCompare(left, right);
    });

    if (extensionInstallationScope) {
        switch (extensionInstallationScope) {
            case UserInstallScope:
                return userInstalledAddinCommands;
            case DefaultInstallScope:
                return defaultInstalledAddinCommands;
            case OrgInstallScope:
                return organizationInstalledAddinCommands;
            default:
                break;
        }
    }

    const orderedItemSendAddinCommands: IAddinCommand[] = [
        ...userInstalledAddinCommands,
        ...defaultInstalledAddinCommands,
        ...organizationInstalledAddinCommands,
    ];
    return orderedItemSendAddinCommands;
}

function addinIdCompare(leftAddinCommand: IAddinCommand, rightAddinCommand: IAddinCommand): number {
    const leftAddinCommandId = leftAddinCommand.get_Id();
    const rightAddinCommandId = rightAddinCommand.get_Id();

    if (leftAddinCommandId < rightAddinCommandId) {
        return -1;
    } else if (leftAddinCommandId > rightAddinCommandId) {
        return 1;
    }
    return 0;
}
